import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import { TFunction } from 'i18next';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  Grid,
  HeaderWithPill,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  MUIDataTableColumn,
  SearchBar,
  TableState,
  Typography,
  useDataTable,
  useSnackbarHandler,
} from 'web-library';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';
import DeleteButton from '../../../../components/buttons/DeleteButton';
import RouteKey from '../../../../routes/key';
import { stringToSemanticDisplay } from '../../../../utils/DateUtils';
import { SelectedCandidate, useCandidateBlacklisting } from '../../logic/useCandidateBlacklisting';
import BulkUploadBlacklistCandidatesModal from './BulkUploadCandidatesModal';
import ExamTypeSingleSelect from './ExamTypeSingleSelect';
import RemoveBlacklistedCandidateModal from './RemoveBlacklistedCandidateModal';

const useStyles = makeStyles()((theme) =>
({
  headerContainer: {
    paddingBottom: theme.spacing(4),
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      paddingBottom: theme.spacing(1),
    },
  },
  addCandidateButton: {
    minWidth: '200px',
  },
  searchContainer: {
    paddingBottom: theme.spacing(3),
  },
  removeButton: {
    color: theme.color.state.stateError,
  },
  addedByText: {
    color: theme.color.text.textTertiary,
  },
  filterSelect: {
    paddingBottom: theme.spacing(3),
  },
}));

const getTableColumns = (
  t: TFunction,
  state: TableState,
  handleDeleteConfirmationModalOpen: () => void,
  setSelectedCandidate: React.Dispatch<React.SetStateAction<SelectedCandidate[]>>,
  classes: Record<string,string>,
  isEnableDelete: boolean
): MUIDataTableColumnDef[] => [
    {
      name: 'candidateId',
      label: t('admin->>CandidateBlacklist.NricFinPassport'),
      options: {
        sort: false,
        searchable: true,
        filter: false,
      },
    },
    {
      name: 'name',
      label: t('admin->>CandidateBlacklist.Name'),
      options: {
        sort: true,
        searchable: true,
        filter: false,
      },
    },
    {
      name: 'dateOfBirth',
      label: t('admin->>CandidateBlacklist.DateOfBirth'),
      options: {
        sort: false,
        searchable: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">
            {stringToSemanticDisplay(state.data[dataIndex].dateOfBirth)}
          </Typography>
        ),
      },
    },
    {
      name: 'examType',
      label: t('admin->>CandidateBlacklist.ExamType'),
      options: {
        sort: false,
        searchable: false,
        filter: true,
        filterType: 'custom',
        filterOptions: {
          fullWidth: true,
          display:  (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <ExamTypeSingleSelect
              // eslint-disable-next-line react/destructuring-assignment
              defaultValue={filterList[index][0]}
              onChange={(event) => {
                // eslint-disable-next-line no-param-reassign
                filterList[index][0] = event;
                onChange(filterList[index], index, column);
              }}
              className={classes.filterSelect}
            />
          ),
        },
      },
    },
    {
      name: 'createdAt',
      label: t('admin->>CandidateBlacklist.DateAdded'),
      options: {
        sort: true,
        searchable: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            <Typography variant="body2">
              {stringToSemanticDisplay(state.data[dataIndex].creationDate)}
            </Typography>
            <Typography className={classes.addedByText} variant="subtitle2">
              Added by {state.data[dataIndex].creatorName}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: 'action',
      label: t('admin->>CandidateBlacklist.Action'),
      options: {
        sort: false,
        searchable: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
          DeleteButton({
            deleteHandler: () => {
              const targetCandidate = state.data[dataIndex];
              setSelectedCandidate([
                {
                  uuid: targetCandidate.uuid,
                  name: targetCandidate.name,
                },
              ]);
              handleDeleteConfirmationModalOpen();
            },
            label: t('component->>IconLabel.Remove'),
            className: classes.removeButton,
          }),
        display: `${isEnableDelete}`,
      },
    },
  ];

const customOptions = (
  tOptions: MUIDataTableOptions,
  remoteTotalRowsSelected: number
): MUIDataTableOptions => ({
  ...tOptions,
  selectableRows: 'multiple',
  pagination: true,
  filter: true,
  sort: true,
  // need to put to make selection replacement work
  selectToolbarPlacement: 'replace',
});

const { candidateBlacklist, addCandidateBlacklist } = RouteKey.admin.child;
const { admin } = RouteKey.child;

const CandidateBlacklisting = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const { classes } = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { state: locationState } = useLocation<{ addedCandidateName: string } | undefined>();
  const { snackbarHandler } = useSnackbarHandler();
  const {
    state,
    dispatch: tableDispatch,
    tOptions,
    remoteTotalRowsSelected,
    selectionToolbar,
  } = useDataTable();
  const {
    isSearchingCandidate,
    candidateDeletionStatus,
    triggerCandidateSearch,
    handleDeleteConfirmationModalOpen,
    handleDeleteConfirmationModalClose,
    onDeleteModalConfirmed,
    onRemoveSelectedClicked,
    openConfirmationModal,
    selectedCandidate,
    setSelectedCandidate,
    isShowModalBulkUpload,
    handleCloseBulkUploadModal,
    handleShowBulkUploadModal,
    numberOfRecords,
    setNumberOfRecords,
  } = useCandidateBlacklisting({ tableDispatch, state });

  const { data: userAuthoritiesData = [] } = useGetUserAuthorities();
  const isEnableCreate = userAuthoritiesData.includes(
    AUTHORITY_NAMES.CP_ADMIN_CANDIDATE_BLACKLIST_CREATE
  );
  const isEnableDelete = userAuthoritiesData.includes(
    AUTHORITY_NAMES.CP_ADMIN_CANDIDATE_BLACKLIST_DELETE
  );

  // Show snackbar after user done adding candidate to blacklist and redirected back to listing page
  useEffect(() => {
    if (locationState && locationState.addedCandidateName) {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>CandidateBlacklist.HasBeenAdded', {
          candidateName: locationState.addedCandidateName,
        }),
      });
      window.history.replaceState({}, document.title);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // Trigger search when table state changed
  useEffect(() => {
    triggerCandidateSearch();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [state.page, state.search, state.size, state.sortColumn, state.order, state.filters]);

  const tableColumns = getTableColumns(
    t,
    state,
    handleDeleteConfirmationModalOpen,
    setSelectedCandidate,
    classes,
    isEnableDelete
  );

  return (
    <ContentTemplate type="full">
      <Box className={classes.headerContainer}>
        <HeaderWithPill
          title={t('admin->>CandidateBlacklist.BlacklistedCandidates')}
          pillValue={state.count.toString()}
        />
        <Box>
          {isEnableCreate && (
            <Button
              id="button-candidateBlacklist-cancel"
              variant="text"
              color="primary"
              startIcon={
                <Icon name={IconName.export} size="S" color={theme.color.icon.iconSelected} />
              }
              onClick={handleShowBulkUploadModal}
              className={classes.addCandidateButton}
            >
              {t('admin->>CandidateBlacklist.BulkUpload')}
            </Button>
          )}
          {isEnableCreate && (
            <Button
              id="button-candidateBlacklist-cancel"
              variant="text"
              color="primary"
              startIcon={
                <Icon name={IconName.add} size="S" color={theme.color.icon.iconSelected} />
              }
              onClick={() => {
                history.push(
                  `/${admin}/${candidateBlacklist.route}/${addCandidateBlacklist.route}`
                );
              }}
              className={classes.addCandidateButton}
            >
              {t('admin->>CandidateBlacklist.AddCandidate')}
            </Button>
          )}
        </Box>
      </Box>

      <Grid xs={12}>
        <Box>
          <SearchBar
            name="searchInput"
            ariaLabel="searchInput"
            placeholder={t('admin->>CandidateBlacklist.SearchPlaceHolder')}
            onSearch={(str) => {
              tableDispatch({
                type: 'UPDATE_SEARCH',
                payload: str,
              });
            }}
            className={classes.searchContainer}
            searchButton={!isMobile}
            clearInputButton
          />
        </Box>
      </Grid>

      <MUIDataTable
        isLoading={isSearchingCandidate}
        // @ts-ignore
        state={state}
        dispatch={tableDispatch}
        columns={tableColumns}
        // @ts-ignore
        data={state.data}
        options={customOptions(tOptions, remoteTotalRowsSelected)}
        components={{
          TableToolbarSelect: () =>
            selectionToolbar([
              <Button
                key="removeSelected"
                variant="text"
                color="primary"
                onClick={onRemoveSelectedClicked}
              >
                {t('admin->>CandidateBlacklist.RemoveSelected')}
              </Button>,
            ]),
        }}
      />

      <RemoveBlacklistedCandidateModal
        modalState={openConfirmationModal}
        onDialogConfirm={onDeleteModalConfirmed}
        onDialogClose={handleDeleteConfirmationModalClose}
        candidateNames={selectedCandidate.map((s) => s.name)}
        apiStatus={candidateDeletionStatus}
      />

      <BulkUploadBlacklistCandidatesModal
        modalState={isShowModalBulkUpload}
        onDialogClose={handleCloseBulkUploadModal}
        displayUploadSuccessSnackbar={(noOfRecords: number) => {
          snackbarHandler?.setGlobalSnackbar({
            targetSeverity: 'success',
            targetData: t('exam->>ExamConfiguration.UpdatedCandidateHistorySuccessfully', {
              numberOfRecords,
            }),
          });
          setNumberOfRecords(noOfRecords);
        }}
        reloadUploadSuccess={triggerCandidateSearch}
      />
    </ContentTemplate>
  );
};

export default CandidateBlacklisting;
